import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Maybe } from 'yup';
import { Alert } from '../../../../shared/lib/common/Alert';
import { NoDataFound } from '../../../../shared/lib/common/NoDataFound';
import { StickyTable } from '../../../../shared/lib/common/StickyTable';
import { GRAPHQL_QUERY_POLICY } from '../../../../shared/lib/constants';
import palette from '../../../../shared/lib/theme/palette';
import { billingWrapper, hasMoreButton, hasMoreButtonWrapper, transactionHistoryHeaderCell } from '../../../../shared/lib/theme/styleConstants';
import { TRANSACTION_HISTORY_TABLE_HEADERS } from '../../app/constants';
import { useAppContext } from '../../app/contexts/AppContext';
import { Invoice, useFetchCustomerInvoicesLazyQuery } from '../../generated';
import { TransactionHistorySkeleton } from '../skeletons/TransactionHistorySkeleton';
import { DisplayTransactionHistoryItem } from './DisplayTransactionHistoryItem';

export const TransactionHistory = () => {
  const { blackTwo } = palette;
  const [transactionHistory, setTransactionHistory] = useState<Maybe<Maybe<Invoice>[]> | undefined>([]);
  const [hasMoreValue, setHasMoreValue] = useState<boolean>(true);
  const [leftArrow, setLeftArrow] = useState<boolean>(false);
  const [firstId, setFirstId] = useState<string>('');
  const [lastId, setLastId] = useState<string>('');
  const [transactionPageCount, setTransactionPageCount] = useState<number>(1);
  const { currentUser } = useAppContext();
  const { isSubscribed } = currentUser ?? {};
  const { customerStripeId } = isSubscribed ?? {};

  const [fetchInvoices, { loading }] = useFetchCustomerInvoicesLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError({ message }) {
      Alert.error(message);
    },

    onCompleted(data) {
      const { fetchCustomerInvoices } = data;
      const { data: fetchTransactionData, has_more } = fetchCustomerInvoices;
      setTransactionHistory(fetchTransactionData);
      setHasMoreValue(has_more);
    }
  });

  const fetchTransactions = async () => {
    customerStripeId &&
      (await fetchInvoices({
        variables: {
          customerInvoiceInput: {
            customer: customerStripeId,
            limit: 10,
            starting_after: lastId ? lastId : undefined,
            ending_before: firstId ? firstId : undefined
          }
        }
      }));
  };

  useEffect(() => {
    (async () => fetchTransactions())();
  }, [currentUser, lastId, firstId]);

  const loadPrevValue = () => {
    const firstIndex = transactionHistory?.[0];
    const { id } = firstIndex ?? {};
    setFirstId(id || '');
    setTransactionPageCount(transactionPageCount - 1);
    setLastId('');
    setLeftArrow(true);
  };

  const loadMoreValue = () => {
    const lastIndex = transactionHistory && transactionHistory[transactionHistory?.length - 1];
    const { id } = lastIndex ?? {};
    setLastId(id || '');
    setFirstId('');
    setTransactionPageCount(transactionPageCount + 1);
    setLeftArrow(false);
  };

  const isEmpty = transactionHistory?.length === 0 && !loading;

  return (
    <Box sx={billingWrapper}>
      {isEmpty ? (
        <NoDataFound height="calc(100vh - 496px)" description="Record not found" />
      ) : (
        <StickyTable height="auto">
          <TableHead>
            <TableRow>
              {TRANSACTION_HISTORY_TABLE_HEADERS.map((item, index) => (
                <TableCell key={index} sx={transactionHistoryHeaderCell}>
                  <Typography variant="subtitle2" sx={{ color: blackTwo }}>
                    {item}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {loading ? (
            <TransactionHistorySkeleton noOfRows={8} />
          ) : (
            <TableBody>{transactionHistory?.map((transaction, index) => <DisplayTransactionHistoryItem key={index} index={index} invoice={transaction} />)}</TableBody>
          )}
        </StickyTable>
      )}

      <Box sx={hasMoreButtonWrapper}>
        <Button sx={hasMoreButton} onClick={loadPrevValue} disabled={transactionPageCount - 1 === 0}>
          <ChevronLeftIcon fontSize="small" />
        </Button>

        <Button sx={hasMoreButton} onClick={loadMoreValue} disabled={transactionPageCount - 1 === 0 ? (leftArrow ? !leftArrow : leftArrow || !hasMoreValue) : !hasMoreValue}>
          <ChevronRightIcon fontSize="small" />
        </Button>
      </Box>
    </Box>
  );
};
